import {type Line} from '../../../util/plotHelpers/types';
import {distributeAreaPoints, distributePoints} from './distributePoints';
import {
  averagePoint,
  maxPoint,
  minPoint,
  type PointReduceFn,
} from './reduceBucket';
import {type AreaPoint, type Point} from './types';

/**
 * What are we doing when we aggregate lines?
 * Each line is collection of points, and each point represents an underlying bin.
 * Lines with aux = true have y & y0 points
 * Lines with aux = false are primary lines and just have y points
 *
 * Aggregation is when we condense N lines of type T into a single line of type T. Because lines come in here with aux and !aux we need to condense them into two new lines: the primary line and its associated min/max range.
 *
 * https://weightsandbiases.slack.com/archives/C062UKHTAHG/p1726084638869859
 */

function pointsFromLines(lines: Line[]) {
  return lines.filter(l => !l.aux).flatMap(line => line.data as Point[]);
}

function areaPointsFromLines(lines: Line[]) {
  return lines.filter(l => l.aux).flatMap(line => line.data as AreaPoint[]);
}

const aggOptionToReduceFn: Record<string, PointReduceFn> = {
  mean: averagePoint,
  min: minPoint,
  max: maxPoint,
};

/**
 * Unions the constituent runs' backend-provided domains (min of minX/minY,
 * max of maxX/maxY), reading only the per-line `.domain` scalars — O(#runs),
 * no point iteration. The union can never be narrower than any run, so one
 * single-point run (minX === maxX) can't collapse the group to xRange 0 and
 * trigger calculateDomain's [0, 2x] axis doubling.
 *
 * Returns undefined if any constituent lacks a finite domain (e.g. a
 * frontend/bucketed line, whose extent lives only in its points). That sends
 * calculateDomain down its point-iteration path, which measures the merged
 * data instead of truncating the domainless line.
 */
function unionDomain(lines: Line[]): Line['domain'] {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const line of lines) {
    const d = line.domain;
    if (
      d == null ||
      !Number.isFinite(d.minX) ||
      !Number.isFinite(d.maxX) ||
      !Number.isFinite(d.minY) ||
      !Number.isFinite(d.maxY)
    ) {
      return undefined;
    }
    if (d.minX < minX) {
      minX = d.minX;
    }
    if (d.maxX > maxX) {
      maxX = d.maxX;
    }
    if (d.minY < minY) {
      minY = d.minY;
    }
    if (d.maxY > maxY) {
      maxY = d.maxY;
    }
  }
  if (minX === Infinity) {
    // empty group — no constituent domains to union
    return undefined;
  }
  return {minX, maxX, minY, maxY};
}

export function aggregateLines<T extends Line>(
  lines: T[],
  meta: {
    groupAgg: string | undefined;
  }
) {
  if (lines.length === 0) {
    return [];
  }

  const baseAvgData = distributePoints(pointsFromLines(lines));
  const minMaxData = distributeAreaPoints(areaPointsFromLines(lines));

  const reduceFn = aggOptionToReduceFn[meta.groupAgg ?? ''] ?? averagePoint;
  const avgData = baseAvgData.map(b => reduceFn(b)).filter(b => !!b);

  const avgLineMeta = lines.find(l => !l.aux);
  const minMaxLineMeta = lines.find(l => l.aux);

  // Override the first run's domain that the meta spreads below would
  // inherit: the group's domain must span ALL constituent runs.
  const domain = unionDomain(lines);

  const toReturn = [
    {
      ...avgLineMeta,
      aux: false,
      data: avgData,
      domain,
      meta: {
        ...avgLineMeta?.meta,
        category: 'grouped',
        mode: 'full-fidelity',
        type: 'line',
      },
    },
    {
      ...minMaxLineMeta,
      data: minMaxData,
      domain,
      meta: {
        ...minMaxLineMeta?.meta,
        aggregation: 'minmax',
        category: 'grouped',
        mode: 'full-fidelity',
        type: 'area',
      },
    },
  ] as T[];

  return toReturn;
}
